import { Button } from '@cms/components/data/Button'
import { SystemPopover } from '@cms/components/data/Popover'
import { renderAttribute } from '@cms/components/renders'
import { generateSearchParam, renderDate, renderDateTime, renderSubtypeLabel, renderTime } from '@cms/utils/helpers'
import { useAppSelector } from '@cms/utils/store/hooks'
import { checkRouteRequiredRole, ISystemCollection, SUBTYPES } from '@dl/declarations'
import clsx from 'clsx'
import { useCallback, useMemo } from 'react'
import { Link } from 'react-router-dom'
import { CreateCollectionForm, UpdateCollectionPage } from '../../components/forms/CollectionForm'
import { DataPart } from '../../components/ui/DataPart'
import PageContainer from '../../components/ui/PageContainer'
import { CollectionsPath, renderRouteOptions, Routes } from '../../utils/routes'

const CreateCollectionMenu = () => {
    return (
        <SystemPopover
            trigger={{
                component: (
                    <Button
                        type='button'
                        inputVariant='white_text_blue_bg'
                    >
                        Create
                    </Button>
                )
            }}
            content={{
                component: (
                    <CreateCollectionForm />
                )
            }}
        />
    )
}

const CollectionsPage = () => {
    const { profile: { permissions } } = useAppSelector((state) => state.user)

    const renderItem = useCallback((collection: ISystemCollection) => {
        return (
            <Link
                className={clsx(
                    'py-4 ps-4 pe-12 w-full',
                    'flex items-center justify-start gap-8',
                    'border border-cs-700 rounded-md',
                )}
                to={{
                    pathname: Routes.COLLECTIONS,
                    search: `?${generateSearchParam(CollectionsPath, "mode", "update")}&${generateSearchParam(CollectionsPath, "id", collection._id)}`,
                }}
                key={collection._id}
            >
                <div
                    className='bg-cs-200 text-cs-900 font-semibold p-1'
                >
                    {renderSubtypeLabel(collection.subtypeObject!, "SHORT")}
                </div>

                <div className='w-full grid grid-cols-6 items-center justify-between gap-4'>
                    <div className='col-span-3 flex flex-col gap-4'>
                        <h4 className='text-cs-600 font-semibold'>{collection.name}</h4>

                        <p>
                            {collection.description}
                        </p>

                    </div>

                    {renderAttribute({
                        title: { value: 'Category' },
                        value: { value: [collection.collection_type], className: 'text-cs-500' }
                    })}

                    {renderAttribute({
                        title: { value: 'Status' },
                        value: { value: [collection.status], className: 'text-cs-500' }
                    })}

                    {renderAttribute({
                        title: { value: 'Updated' },
                        value: {
                            value: [renderDateTime(collection.updated_at)],
                            component: (
                                <div className='flex flex-col'>
                                    <p className='font-semibold'>{renderDate(collection.updated_at)}</p>
                                    <p>{renderTime(collection.updated_at)}</p>
                                </div>
                            )
                        }
                    })}
                </div>
            </Link>
        )
    }, [])

    const [allowCreate, allowUpdate] = useMemo(() => {
        return [checkRouteRequiredRole(CollectionsPath, "c", permissions), checkRouteRequiredRole(CollectionsPath, "u", permissions)]
    }, [permissions])

    return (
        <PageContainer
            title={renderRouteOptions(Routes.COLLECTIONS).title}
        >
            <DataPart<ISystemCollection>
                tableSize='full'

                distribution={{
                    type: 'grid',
                    renderItem: renderItem,
                    noOfColumns: "1",
                }}

                filters={{
                    subtypes: {
                        type: 'select',
                        label: 'Subtypes',
                        allowMultiSelect: true,
                        options: [],
                        hide: true
                    },
                    collection_type: {
                        type: 'select',
                        label: 'All categories',
                        allowMultiSelect: false,
                        searchOptions: {
                            allow: false
                        },
                        options: [
                            // { _id: 'all', slug: 'all', label: 'All categories', groupKey: 'Options' },
                            { _id: '1', slug: 'Story', label: 'Stories', groupKey: 'Options' },
                            { _id: '2', slug: 'Report', label: 'Reports', groupKey: 'Options' },
                            { _id: '3', slug: 'Event', label: 'Events', groupKey: 'Options' },
                            { _id: '4', slug: 'User', label: 'Users', groupKey: 'Options' },
                        ]
                    }
                }}

                queryOptions={{
                    basePath: CollectionsPath,
                    initialParams: {
                        sort: 'updated_at:desc',
                    }
                }}

                updateOptions={{
                    allow: allowUpdate,
                    hide: true,
                }}
                createOptions={{
                    hide: !allowCreate,
                    allow: allowCreate,
                    component: <CreateCollectionMenu />
                }}
                searchOptions={{
                    allow: true
                }}

                subtypes={[
                    SUBTYPES.NEWS,
                    SUBTYPES.RESEARCH,
                    SUBTYPES.SYSTEM,
                ]}

                form={<UpdateCollectionPage />}
            />
        </PageContainer>
    )
}

export default CollectionsPage